import type { AttendanceStatus, RollCallResult, SessionBoard } from '@shared/types/api'

/** Как называть статус в интерфейсе. */
export interface StatusMeta {
  /** Подпись в строке списка */
  label: string
  /** Короткая подпись для узких мест */
  short: string
  /** CSS-переменная цвета точки */
  color: string
  /** Засчитывается ли как посещение */
  attended: boolean
  /** Показывать ли статус на первом уровне (тап), а не в длинном нажатии */
  primary: boolean
}

export const ATTENDANCE_STATUS_META: Record<AttendanceStatus, StatusMeta> = {
  present: { label: 'был', short: 'был', color: 'var(--success)', attended: true, primary: true },
  absent: { label: 'не был', short: 'нет', color: 'var(--danger)', attended: false, primary: true },
  late: { label: 'опоздал', short: 'опоздал', color: 'var(--accent)', attended: true, primary: true },
  left_early: {
    label: 'ушёл раньше',
    short: 'ушёл',
    color: 'var(--accent)',
    attended: true,
    primary: false,
  },
  sick: { label: 'болеет', short: 'болеет', color: 'var(--text-hint)', attended: false, primary: false },
  excused: {
    label: 'уважительная',
    short: 'уваж.',
    color: 'var(--text-hint)',
    attended: false,
    primary: false,
  },
}

/** Основные статусы — то, что видно сразу. */
export const PRIMARY_STATUSES: AttendanceStatus[] = (
  Object.keys(ATTENDANCE_STATUS_META) as AttendanceStatus[]
).filter((s) => ATTENDANCE_STATUS_META[s].primary)

/** Остальные статусы — из длинного нажатия. */
export const SECONDARY_STATUSES: AttendanceStatus[] = (
  Object.keys(ATTENDANCE_STATUS_META) as AttendanceStatus[]
).filter((s) => !ATTENDANCE_STATUS_META[s].primary)

export function statusLabel(status?: AttendanceStatus): string {
  return status ? ATTENDANCE_STATUS_META[status].label : ''
}

export function statusColor(status?: AttendanceStatus): string {
  return status ? ATTENDANCE_STATUS_META[status].color : 'var(--separator)'
}

export function countsAsAttended(status?: AttendanceStatus): boolean {
  return status ? ATTENDANCE_STATUS_META[status].attended : false
}

/**
 * Что происходит по одному тапу в строке списка.
 *
 * Неотмеченный становится присутствующим, присутствующий — отсутствующим,
 * отсутствующий снимается совсем. Уточняющие статусы (опоздал, болеет)
 * тап не трогает: их ставят длинным нажатием и случайно сбросить их нельзя.
 */
export function nextToggleStatus(current?: AttendanceStatus): AttendanceStatus | null {
  switch (current) {
    case undefined:
      return 'present'
    case 'present':
      return 'absent'
    case 'absent':
      return null
    default:
      return 'present'
  }
}

/** Заголовок занятия: «Соколята · 3 сентября, 17:00». */
export function formatSessionTitle(board: Pick<SessionBoard, 'group_name' | 'date' | 'start_time'>): string {
  const parts = [board.group_name, formatSessionDate(board.date)]
  const time = formatTimeOfDay(board.start_time)
  if (time) parts[1] = `${parts[1]}, ${time}`
  return parts.filter(Boolean).join(' · ')
}

const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
]

/** «2026-09-03» → «3 сентября». Пустая или битая дата даёт пустую строку. */
export function formatSessionDate(date?: string): string {
  if (!date) return ''
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date)
  if (!match) return ''
  const month = Number(match[2]) - 1
  if (month < 0 || month > 11) return ''
  return `${Number(match[3])} ${MONTHS[month]}`
}

/**
 * Время начала приходит с бэкенда как время суток с нулевой датой
 * («0000-01-01T17:00:00Z»), поэтому берём из строки только часы и минуты.
 */
export function formatTimeOfDay(value?: string): string {
  if (!value) return ''
  const match = /(\d{2}):(\d{2})/.exec(value)
  return match ? `${match[1]}:${match[2]}` : ''
}

/** «12 из 15» — счётчик отмеченных вверху экрана. */
export function formatProgress(board: Pick<SessionBoard, 'marked_count' | 'total_count'>): string {
  return `${board.marked_count} из ${board.total_count}`
}

/** Процент посещаемости, округлённый до целых. */
export function formatRate(rate: number): string {
  return `${Math.round(rate)}%`
}

/**
 * Строка подтверждения после голосовой переклички.
 *
 * Обязательно называет тех, кого не нашли или не смогли опознать однозначно:
 * в зале шумно, и тихо применённая неверная отметка хуже отсутствия отметки.
 */
export function summarizeRollCall(result: RollCallResult): {
  marked: string
  problems: string[]
} {
  const marked = result.marked.length
    ? `Отметил: ${result.marked.map((m) => m.name).join(', ')}`
    : 'Никого не отметил'

  const problems: string[] = []
  if (result.not_found?.length) {
    problems.push(`Не нашёл: ${result.not_found.map((n) => `«${n}»`).join(', ')}`)
  }
  for (const item of result.ambiguous ?? []) {
    problems.push(`«${item.query}» — это ${item.candidates.join(' или ')}?`)
  }

  return { marked, problems }
}

/** Сегодняшняя дата в формате, который понимает бэкенд. */
export function todayISO(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}
